import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
// @mui
import { Box, Button } from '@mui/material';
// i18n
import { useTranslation } from 'react-i18next';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useLocales from '../../locales/useLocales';
// auth
import { useAuthContext } from '../../auth/useAuthContext';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { fetchSubOrgById } from '../../redux/features/subOrganization/subOrgSlice';
import { selectSubOrg } from '../../redux/features/subOrganization/subOrgSelectors';
// components
import { useSettingsContext } from '../../components/settings';
import ConfirmDialog from '../../components/confirm-dialog';
import { OrganizationSyncStatus } from '../../@enums/OrganizationSyncStatus';
//
import Main from './Main';
import Header from './header';
import NavMini from './nav/NavMini';
import NavVertical from './nav/NavVertical';
import NavHorizontal from './nav/NavHorizontal';

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { themeLayout } = useSettingsContext();
  const { user } = useAuthContext();
  const dispatch = useDispatch();
  const subOrg = useSelector(selectSubOrg);
  const { onChangeLang } = useLocales();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const langApplied = useRef(false);
  const [openNoPosConfig, setOpenNoPosConfig] = useState(false);
  const isManager = user?.role === 'MANAGER' || user?.role === '12';

  // Fetch the user's sub-org once when they enter the dashboard
  useEffect(() => {
    if (user?.subOrgId) {
      dispatch(fetchSubOrgById(user.subOrgId));
    }
  }, [user?.subOrgId, dispatch]);

  // Apply the org's default language once per mount (i.e., once per login session)
  // On vérifie que subOrg.id === user.subOrgId pour éviter d'appliquer
  // des données stale d'une org précédente encore dans Redux au moment du login
  useEffect(() => {
    if (!subOrg?.defaultLanguage || langApplied.current) return;
    if (subOrg.id !== user?.subOrgId) return; // données stale → on attend la bonne org
    onChangeLang(subOrg.defaultLanguage.toLowerCase()); // 'FR' → 'fr'
    langApplied.current = true;
  }, [subOrg?.defaultLanguage, subOrg?.id, user?.subOrgId, onChangeLang]);

  // Show POS not configured popup once per session for managers
  useEffect(() => {
    if (isManager && subOrg && subOrg.id === user?.subOrgId) {
      const isConfigured = 
        subOrg.posSyncStatus === OrganizationSyncStatus.CONFIGURED || 
        subOrg.posSyncStatus === OrganizationSyncStatus.SYNCED;
      const hasSeenWarning = sessionStorage.getItem(`pos_warning_${user?.subOrgId}`);

      if (!isConfigured && !hasSeenWarning) {
        setOpenNoPosConfig(true);
        sessionStorage.setItem(`pos_warning_${user?.subOrgId}`, 'true');
      }
    }
  }, [subOrg, isManager, user?.subOrgId]);

  const isDesktop = useResponsive('up', 'lg');

  const [open, setOpen] = useState(false);

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const renderNavVertical = <NavVertical openNav={open} onCloseNav={handleClose} />;

  if (isNavHorizontal) {
    return (
      <>
        <Header onOpenNav={handleOpen} />

        {isDesktop ? <NavHorizontal /> : renderNavVertical}

        <Main>
          <Outlet />
        </Main>
      </>
    );
  }

  if (isNavMini) {
    return (
      <>
        <Header onOpenNav={handleOpen} />

        <Box
          sx={{
            display: { lg: 'flex' },
            minHeight: { lg: 1 },
          }}
        >
          {isDesktop ? <NavMini /> : renderNavVertical}

          <Main>
            <Outlet />
          </Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={handleOpen} />

      <Box
        sx={{
          display: { lg: 'flex' },
          minHeight: { lg: 1 },
        }}
      >
        {renderNavVertical}

        <Main>
          <Outlet />
        </Main>
      </Box>

      <ConfirmDialog
        open={openNoPosConfig}
        onClose={() => setOpenNoPosConfig(false)}
        title={t('pos.not_configured_title')}
        content={t('pos.not_configured_content')}
        action={
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              setOpenNoPosConfig(false);
              if (isManager && user?.parentOrgId && user?.subOrgId) {
                navigate(`/dashboard/organization/${user.parentOrgId}/branches/${user.subOrgId}/settings`);
              }
            }}
          >
            {t('nav.go_to_settings')}
          </Button>
        }
      />
    </>
  );
}
